import { useEffect, useRef, useState } from 'react';
import type { Timer } from '../types';
import { getServerTime } from '../utils/serverTime';

/**
 * Calcule le temps statique actuel pour un timer donné.
 */
export function computeTimerDisplayMs(timer: Timer | null): number {
  if (!timer) return 0;

  if (timer.state === 'running' && timer.startedAt !== null) {
    const elapsed = (timer.accumulatedMs || 0) + (getServerTime() - timer.startedAt);
    if (timer.type === 'countdown' && timer.countdownDurationMs !== null) {
      return timer.countdownDurationMs - elapsed;
    }
    return elapsed;
  }

  if (timer.state === 'paused') {
    if (timer.type === 'countdown' && timer.countdownDurationMs !== null) {
      return timer.countdownDurationMs - timer.accumulatedMs;
    }
    return timer.accumulatedMs;
  }

  if (timer.state === 'idle') {
    if (timer.type === 'countdown' && timer.countdownDurationMs !== null) {
      return timer.countdownDurationMs;
    }
    return 0;
  }

  if (timer.state === 'finished') {
    if (timer.type === 'countdown') {
      return 0;
    }
    return timer.accumulatedMs;
  }

  return 0;
}

/**
 * Hook qui calcule et retourne le temps d'affichage actuel pour un chronomètre.
 * Utilise requestAnimationFrame uniquement lorsque le timer est 'running'.
 * N'écrit pas dans Firebase — affichage pur.
 */
export function useTimerDisplay(timer: Timer | null): number {
  const [displayMs, setDisplayMs] = useState(() => computeTimerDisplayMs(timer));
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (!timer || timer.state !== 'running') {
      return;
    }

    const loop = () => {
      setDisplayMs(computeTimerDisplayMs(timer));
      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [timer]);

  // Si le timer n'est pas 'running', dérivation directe (zéro effet, zéro lag)
  if (!timer || timer.state !== 'running') {
    return computeTimerDisplayMs(timer);
  }

  return displayMs;
}
